// Expanded case-study data with long-form content that supports individual
// case-study detail pages. Each case study includes challenge / solution /
// results narratives plus a headline metrics grid for AEO passage extraction.

export const CASE_STUDY_DETAILS = {
  'ecommerce-revenue-growth': {
    client: 'Meridian DTC (Beauty & Wellness)',
    region: 'North America',
    duration: '14 months',
    heroDesc:
      'A DTC beauty brand hit a growth plateau at $1.2M annual paid revenue. Meta CPMs were rising, signal loss from iOS 14.5 was distorting attribution, and creative fatigue was killing return on ad spend. Ransen rebuilt the tracking stack, deployed a high-velocity creative testing engine, and consolidated the account into Meta Advantage+ — scaling paid revenue to $9.8M within 14 months.',
    metrics: [
      { label: 'ROAS lift', value: '4.6x' },
      { label: 'Paid revenue', value: '$1.2M → $9.8M' },
      { label: 'CAC reduction', value: '-38%' },
      { label: 'Creative velocity', value: '48 variants/mo' },
    ],
    challenge: [
      'Meta Pixel-only tracking was undercounting conversions by an estimated 32%, distorting bid signals and CAC.',
      'Fewer than 8 creative variants per month were shipping — well below the fatigue threshold for a scaled Meta program.',
      'Attribution disputes with the internal team were slowing budget decisions to 6+ week cycles.',
      'A patchwork Shopify + warehouse setup meant customer LTV wasn’t flowing back into bid optimization.',
    ],
    solution: [
      'Deployed server-side GTM on Google Cloud with a custom first-party subdomain. Wired Meta CAPI, TikTok Events API, and Google Enhanced Conversions.',
      'Built a 45-day creative sprint cadence: 12 briefs per month, split 40% static / 30% video / 20% UGC / 10% experimental.',
      'Migrated Meta account to Advantage+ Shopping Campaigns and consolidated 26 ad sets into 3 asset groups.',
      'Streamed order + LTV data from Shopify → BigQuery → Meta Conversions API with value-based bidding tied to 90-day LTV.',
    ],
    results: [
      'Blended ROAS grew from 1.9x to 4.6x within 14 months — sustained across three consecutive quarters.',
      'Reported conversions rose 34% within the first month of CAPI deployment; Meta match quality climbed from 5.2 → 8.9.',
      'Creative velocity increased 6x (from 8 to 48 variants per month); creative fatigue no longer capped account performance.',
      'Weekly reporting shifted from a 90-minute analyst pull to an automated Monday-morning dashboard the founding team reads in 5 minutes.',
    ],
    tags: ['Meta Ads', 'CAPI', 'Creative', 'Advantage+', 'Server-Side Tracking'],
  },

  'saas-demand-generation': {
    client: 'Nova SaaS (Enterprise B2B)',
    region: 'North America + Europe',
    duration: '11 months',
    heroDesc:
      'An enterprise B2B SaaS platform was generating leads but not pipeline. MQL-to-opportunity conversion was under 8%, sales was frustrated by low lead quality, and marketing was optimizing to form fills that never became revenue. Ransen rebuilt the demand engine — full-funnel LinkedIn + Google Ads with ABM overlays, offline conversion imports, and a lifecycle model tied to CRM revenue.',
    metrics: [
      { label: 'CAC reduction', value: '-68%' },
      { label: 'SQL rate', value: '2.4x' },
      { label: 'Pipeline / $ ad spend', value: '3.9x' },
      { label: 'Sales cycle', value: '-22 days' },
    ],
    challenge: [
      'Lead volume looked healthy but under 8% converted to opportunities — sales was quietly ignoring inbound.',
      'Meta was cannibalizing intent-driven Google traffic; blended CAC looked stable but hid a 40% waste layer.',
      'LinkedIn Ads was optimizing to form fills, not pipeline; the algorithm was scaling the wrong outcomes.',
      'Attribution stopped at first click and marketing had no visibility into which programs generated closed-won revenue.',
    ],
    solution: [
      'Rebuilt the ICP definition with sales, then built matched-audience lists in LinkedIn Ads covering 4,800 named target accounts.',
      'Wired HubSpot ↔ LinkedIn Ads offline conversion import for MQL, SQL, opportunity, and closed-won events with click-ID stitching.',
      'Split Google Ads into brand, competitor, non-brand-commercial, and problem-aware search campaigns. Fully sunsetted Meta prospecting.',
      'Deployed a lifecycle attribution model in BigQuery + Looker joining ad platform, HubSpot, and Salesforce data.',
    ],
    results: [
      'CAC dropped 68% within two quarters — driven by killing the Meta prospecting waste layer and refocusing LinkedIn on target accounts.',
      'SQL rate lifted 2.4x once LinkedIn started optimizing toward SQL events instead of form fills.',
      'Pipeline generated per ad-spend dollar climbed to 3.9x industry benchmark by month 8.',
      'Sales cycle compressed 22 days on average, driven by better lead qualification and more contextual outreach.',
    ],
    tags: ['LinkedIn Ads', 'ABM', 'HubSpot', 'Attribution', 'B2B SaaS'],
  },

  'real-estate-lead-generation': {
    client: 'Harbor Homes (Multi-Market Home Builder)',
    region: 'North America (4 metros)',
    duration: '6 months',
    heroDesc:
      'A multi-market home builder was running Google + Meta Ads across four metros with no unified tracking. Every regional office had different reporting, budget decisions took weeks, and no one could confirm which campaigns were driving actual appointments and sales. Ransen rebuilt tracking, launched geo-targeted campaigns, and shipped a CRO program that tripled booked appointments in six months.',
    metrics: [
      { label: 'Qualified leads', value: '+312%' },
      { label: 'Booked appointments', value: '3.1x' },
      { label: 'Cost per appointment', value: '-54%' },
      { label: 'CRM close rate', value: '+41%' },
    ],
    challenge: [
      'Four regional offices ran four different Google Ads + Meta accounts with no unified conversion tracking.',
      'Landing pages were built by the developer of each project; conversion rate ranged from 0.8% to 4.2% with no consistent CRO logic.',
      'Marketing reported form fills; sales tracked booked appointments; nobody agreed which number represented reality.',
      'Rising CPMs in home categories were pushing per-lead cost 3x industry benchmark.',
    ],
    solution: [
      'Consolidated four Google Ads and four Meta accounts into a single MCC with geo-tiered campaign structure per metro.',
      'Deployed server-side tracking with offline conversion imports from the CRM back to Google Ads and Meta.',
      'Rebuilt landing pages on a shared component library with A/B testing baked in; ran 22 experiments across the 6-month window.',
      'Introduced weekly cost-per-appointment reporting tied directly to the CRM — killed the metric ambiguity between marketing and sales.',
    ],
    results: [
      'Qualified leads grew 312% within the six-month program with a 54% reduction in cost per appointment.',
      'Booked appointments tripled (3.1x) as landing page conversion rate climbed from 1.6% to 5.4% blended.',
      'CRM close rate lifted 41% because bid signals now optimized to booked appointments, not form fills.',
      'Regional office reporting standardized to a single weekly view — budget decisions now happen in a Monday call, not a Slack thread.',
    ],
    tags: ['Google Ads', 'Meta Ads', 'CRO', 'Real Estate', 'Local SEO'],
  },

  'seo-llm-visibility': {
    client: 'A vertical SaaS platform (undisclosed)',
    region: 'Global',
    duration: '9 months',
    heroDesc:
      'A vertical SaaS platform in a competitive category was ranking well organically but losing share as AI assistants began mediating category-defining queries. ChatGPT rarely mentioned the brand; Perplexity cited competitors first. Ransen merged entity-based SEO with a full LLMO program — result: 4.1x organic traffic growth and 220+ tracked AI citations across ChatGPT, Claude, Perplexity, and Gemini.',
    metrics: [
      { label: 'Organic traffic', value: '4.1x' },
      { label: 'AI citations', value: '220+ tracked' },
      { label: 'Wikipedia + Wikidata', value: 'Published' },
      { label: 'Brand mention share', value: '18% → 47%' },
    ],
    challenge: [
      'Ranking well on Google but losing share as AI Overviews and ChatGPT recommendations shifted user journeys.',
      'Brand had no Wikipedia article, weak Wikidata entry, and thin third-party mentions in category-defining outlets.',
      'Content library was topical-broad, topical-shallow — 200 articles across 40 topics, none authoritative.',
      'Zero visibility on which prompts across ChatGPT, Claude, Perplexity, and Gemini were mentioning the brand.',
    ],
    solution: [
      'Built the LLMO prompt map — 180 category-defining prompts, baseline citation state, weekly monitoring.',
      'Published and iterated Wikipedia article + Wikidata entity, tightened Crunchbase, G2, Capterra profiles.',
      'Consolidated 200 articles into 45 deep entity pages across 5 dominant topics; refreshed with first-party research and named-author bylines.',
      'Ran a research-syndication motion — one original data study per quarter placed in 3 tier-1 outlets each.',
    ],
    results: [
      'Organic traffic grew 4.1x over 9 months with compounding gains still accelerating at end of engagement.',
      '220+ AI citations tracked across ChatGPT, Claude, Perplexity, and Gemini for target category prompts.',
      'Brand mention share on the prompt map climbed from 18% baseline to 47% within 8 months.',
      'Direct + organic traffic from AI assistant referrers (chatgpt.com, perplexity.ai) grew 12x from a small base.',
    ],
    tags: ['SEO', 'LLMO', 'Entity SEO', 'Content Strategy', 'Digital PR'],
  },
};
